import * as THREE from 'three';
import {
  boatLayout,
  BOTE_RADIUS,
  createClearingMap,
  elderLayout,
  hexLen,
  hexToWorld,
  hullCells,
  swellLayout,
  wakeFork,
  worldToHex,
  type BattleState,
  type CazaPt,
  type MapDef,
  type WakeFork,
} from '@somnhold/sim';
import { mergeGeometries } from 'three/addons/utils/BufferGeometryUtils.js';
import { Kit, place } from '../seasons/kit.ts';
import { lookPoint, looksRng, Precip, type OmenCtx } from '../omens/common.ts';
import { BOAR_HIP_Y, BOAR_LEGS, boarBody, boarLeg } from '../models/boar.ts';
import { elderHowler, grayWolf } from '../models/caza.ts';
import { facingToRotY, SCALE, WORLD_PER_HEX } from './coords.ts';
import { GlowLayer } from './glow.ts';
import { SkyDome, smoothTiles } from './snow.ts';
import { iceMaterial, iceSheetGeometry, releaseIceTexture } from './ice-sheet.ts';
import type { TerrainBuild } from './terrain.ts';

/**
 * The Long Hunt's chapter 3 places (docs/sendas/barbaro.md, «El bote»): Bera's long boat out on the open sea after the
 * Grey Wolf. The boat is drawn whole over the board (the hull's cells are its deck): low and open, six oars, shields
 * on the gunwale, a boar's head carved on the prow, Bera at the tiller.
 *
 * - `boteCamp` (the cinematic), `boteShore` (the opening): the tail's tip by the sea at dusk, the clan's camp coming
 *   down (carts loaded, hides tied, the herd of stone boars in a line toward the flank), the boat at the water.
 * - `boteWake` (act 1): a cold grey day on a moving sea, floes drifting by; at each fork four great floes part in three
 *   channels ahead of the bow, the current's arrows on the water, the Wolf's broad wake of grey ash through one (or a
 *   smear at a mouth where the pack swam over it), the pack's thin wakes; the world slides the boat into the one taken.
 *   Ivar's red-prowed boat in another channel, the Wolf's head far ahead. The oars row; a bitten one hangs broken.
 * - `boteCalm`: the sea gone still, a floe with the old howler on it looking at the boat.
 * - `boteSwell` (act 2): a storm by night, moonlit; the bow rope, four floes off the sides; each big wave a crest coming
 *   at the bow and breaking over the deck; who the water takes, a head in the sea.
 * - `boteFire`: the boat at night, the sail rigged low as a tent over the middle, a fire-pot.
 * - `boteElder` (act 3, the test): storm and lightning, four floes joined to the boat, the dark water off two tips at a
 *   time (the sea's shader), the howler's look at its tip (red), the leap's shadow, the Grey Wolf circling far off.
 * - `boteDawn`: a white dawn, the White Island under ice to the north, Ivar's boat run aground on its shore.
 *
 * Terrón is drawn here (the herd's body and legs). Draw calls: the dressing (one merged mesh), the oars (six), the
 * fork's floes and wakes (a few, rebuilt once a fork), the drift floes (one instanced), the wave's crest, the leap's
 * shadow, the sea, the sky, the glow layer, the rain. No lights of its own: the board's are repainted (HOURS).
 */

export const BOTE_PLACES = new Set(['boteCamp', 'boteShore', 'boteWake', 'boteCalm', 'boteSwell', 'boteFire', 'boteElder', 'boteDawn']);

const S = WORLD_PER_HEX;
const K = S / 1000;
const HR = S / Math.sqrt(3);
const RED = new THREE.Color('#ff4f3a');
const RED_SOFT = new THREE.Color('#ff8a6e');
const COLD = new THREE.Color('#dfeaf6');
const FIRE = new THREE.Color('#ff9a5a');
const FIRE_CORE = new THREE.Color('#fff0e6');
const ICE = ['#dfe8ef', '#d2dee7', '#e8eff4', '#c8d6e1'];
const ICE_SIDE = ['#9fb6c8', '#8ea7bb', '#adc2d2'];
const WOOD = ['#4a3d32', '#3e342b', '#55473a', '#5d4d3e'];
const PLANK = ['#5a4b3d', '#524537', '#615142'];
const STONE = ['#6c737b', '#5d646c', '#737a82'];
const CLAN = '#a43a2c';
const TERRON = new THREE.Color('#c9d6c4');

type Hour = 'day' | 'dusk' | 'storm' | 'night' | 'dawn';

const HOURS: Record<Hour, { bg: string; fog: [string, number, number]; hemi: [string, string, number]; key: [string, number]; rim: [string, number]; sky: [string, string, string]; sea: [string, string, string]; dark: string }> = {
  // A cold grey day over a moving sea: readable, the ash grey on blue-grey water.
  day: { bg: '#66737f', fog: ['#7d8a96', 62, 175], hemi: ['#d9e2ec', '#3b4148', 2.0], key: ['#f2f6fc', 1.7], rim: ['#a8bad0', 0.8], sky: ['#43566a', '#9fadbc', '#eef2f8'], sea: ['#2a4a5e', '#55798c', '#e8f0f6'], dark: '#0c1820' },
  // The red dusk over the tail (the stone and the ice kept cold under it).
  dusk: { bg: '#2b1d24', fog: ['#2c303a', 34, 120], hemi: ['#c2cee2', '#2a2f36', 2.3], key: ['#ff9a78', 1.0], rim: ['#8fa0e0', 1.1], sky: ['#1e1828', '#c4533c', '#ffb28c'], sea: ['#1f2a3a', '#465468', '#dfe2ea'], dark: '#0b1018' },
  // A storm by night, moonlit: never black, the boat and the floes read against the sea.
  storm: { bg: '#1c2431', fog: ['#232c3a', 30, 100], hemi: ['#a9b9d2', '#1d222b', 2.1], key: ['#cfdcef', 1.25], rim: ['#86a0cc', 1.05], sky: ['#0e131c', '#2b3649', '#6a7b96'], sea: ['#13202c', '#2f4658', '#cbd7e3'], dark: '#03070c' },
  // Night under the sail: the fire-pot's warmth, the sea cold.
  night: { bg: '#151a24', fog: ['#1a202b', 26, 90], hemi: ['#8f9dba', '#181b22', 1.7], key: ['#ffb08a', 0.7], rim: ['#7f95c0', 0.9], sky: ['#0c1018', '#1e2636', '#4c5a72'], sea: ['#0f1923', '#25384a', '#aebccb'], dark: '#05090e' },
  // A white dawn: pale, cold, the ice white.
  dawn: { bg: '#c3ccd6', fog: ['#d0d8e1', 34, 130], hemi: ['#eef3f9', '#5b6470', 2.15], key: ['#fff3ea', 1.55], rim: ['#bcd0ea', 0.8], sky: ['#9aabbf', '#dbe3ec', '#ffffff'], sea: ['#3d5666', '#7a95a5', '#f4f8fb'], dark: '#1b2a35' },
};

const hourOf = (place: string): Hour =>
  place === 'boteWake' ? 'day' : place === 'boteSwell' || place === 'boteElder' ? 'storm' : place === 'boteFire' ? 'night' : place === 'boteDawn' ? 'dawn' : 'dusk';

/**
 * The sea: a swell, broken foam, a sheen; it flows past the boat (act 1), roughens in a storm, and darkens where the
 * deep water is (act 3: four spots, each with its own strength, eased by the layer).
 */
export const seaMat = (): THREE.ShaderMaterial =>
  new THREE.ShaderMaterial({
    uniforms: {
      time: { value: 0 },
      flow: { value: 0 },
      rough: { value: 0 },
      deep: { value: new THREE.Color() },
      shallow: { value: new THREE.Color() },
      foamC: { value: new THREE.Color() },
      darkC: { value: new THREE.Color() },
      darkP: { value: [new THREE.Vector3(), new THREE.Vector3(), new THREE.Vector3(), new THREE.Vector3()] },
      flash: { value: 0 },
    },
    fog: false,
    vertexShader: /* glsl */ `
      varying vec2 vXZ; varying float vD;
      void main() { vec4 w = modelMatrix * vec4(position, 1.0); vXZ = w.xz; vec4 v = viewMatrix * w; vD = -v.z; gl_Position = projectionMatrix * v; }`,
    fragmentShader: /* glsl */ `
      uniform float time; uniform float flow; uniform float rough; uniform float flash;
      uniform vec3 deep; uniform vec3 shallow; uniform vec3 foamC; uniform vec3 darkC; uniform vec3 darkP[4];
      varying vec2 vXZ; varying float vD;
      float h(vec2 p) { p = mod(p, 289.0); return fract(sin(dot(p, vec2(127.1, 311.7))) * 43758.5453); }
      float n(vec2 p) { vec2 i = floor(p), f = fract(p); vec2 u = f * f * (3.0 - 2.0 * f);
        return mix(mix(h(i), h(i + vec2(1, 0)), u.x), mix(h(i + vec2(0, 1)), h(i + vec2(1, 1)), u.x), u.y); }
      void main() {
        vec2 p = vXZ - vec2(0.0, flow);
        float sw = n(p * 0.16 + vec2(time * 0.07, time * 0.03)) * 0.6 + n(p * 0.5 - vec2(time * 0.12, -time * 0.05)) * 0.4;
        float fn = n(vec2(p.x * 2.0 - time * 0.35, p.y * 0.8 + sw * 3.0)) * n(p * 1.6 + time * 0.1);
        float foam = smoothstep(0.44 - rough * 0.08, 0.56, fn) * smoothstep(0.45 - rough * 0.2, 0.7, sw);
        vec3 col = mix(deep, shallow, sw * (0.8 + rough * 0.3));
        col = mix(col, foamC, foam * (0.38 + rough * 0.12));
        // The deep water: an inky pool, its edge ragged and moving, ringed with pale foam turning round it.
        float dk = 0.0;
        float rim = 0.0;
        for (int i = 0; i < 4; i++) {
          vec2 d = vXZ - darkP[i].xy;
          float r = length(d) + (n(vXZ * 0.7 + time * 0.2) - 0.5) * 1.6;
          dk = max(dk, darkP[i].z * (1.0 - smoothstep(3.0, 5.2, r)));
          float a = atan(d.y, d.x);
          float sp = 0.5 + 0.5 * sin(a * 5.0 + r * 1.3 - time * 1.4);
          rim = max(rim, darkP[i].z * smoothstep(0.9, 0.0, abs(r - 5.4)) * (0.45 + 0.55 * sp));
        }
        col = mix(col, darkC, dk * 0.92);
        col = mix(col, foamC, rim * 0.75);
        col = mix(col, shallow * 1.1, smoothstep(70.0, 170.0, vD) * 0.6);
        col += vec3(0.55, 0.62, 0.75) * flash * 0.35;
        gl_FragColor = vec4(col, 1.0);
      }`,
  });

/* ---------- the scenes' grounds ---------- */

const cellsOf = (R: number): { q: number; r: number }[] => {
  const out: { q: number; r: number }[] = [];
  for (let r = -R; r <= R; r++) for (let q = -R; q <= R; q++) if (hexLen(q, r) <= R) out.push({ q, r });
  return out;
};

/** The scenes' extra floor besides the hull: the tail's shore, the calm's floe. */
const sceneFloor = (place: string): { q: number; r: number }[] => {
  const out: { q: number; r: number }[] = [];
  const R = BOTE_RADIUS;
  for (const h of cellsOf(R)) {
    const p = hexToWorld(h.q, h.r);
    if ((place === 'boteShore' || place === 'boteCamp') && p.y >= 6000) out.push(h);
    if (place === 'boteCalm' && (p.x - 4500) ** 2 + (p.y + 4800) ** 2 <= 1700 ** 2) out.push(h);
  }
  return out;
};

/** A scene's board (client game/cine.ts): its floor the boat's (and the shore's, the floe's), the rest the sea. */
export const boteSceneMap = (place: string): MapDef | null => {
  if (!BOTE_PLACES.has(place) || place === 'boteWake' || place === 'boteSwell' || place === 'boteElder') return null;
  const m = createClearingMap(BOTE_RADIUS);
  const floor = new Set([...(place === 'boteCamp' ? [] : hullCells()), ...sceneFloor(place)].map((h) => `${h.q},${h.r}`));
  return { ...m, rocks: cellsOf(m.radius).filter((h) => !floor.has(`${h.q},${h.r}`)) };
};

/** The scenes' boards' seed (client game/cine.ts builds the same). */
export const BOTE_SCENE_SEED = 7413;

/** How far apart the fork's channels lie across the bow (world units). */
export const BOTE_LANE = 5.4;

/**
 * Where the fork in sight stands (world z of its middle; its channels' mouths 4.5 nearer the bow), coming at the boat as
 * the look runs out: the layer eases toward it, the plate's marks sit on it (game/bote-hud.ts). Null with none in sight.
 */
export const boteForkZ = (s: BattleState): number | null => {
  const r = s.rules?.senda?.bote?.wake;
  const w = s.senda?.bote?.wake;
  if (!r || !w || !wakeFork(s)) return null;
  const k = Math.max(0, Math.min(1, (s.waveT - w.at) / Math.max(1, r.look)));
  return -22 + k * 12;
};

/* ---------- the layer ---------- */

interface Oar {
  g: THREE.Group;
  blade: THREE.Object3D;
  side: number;
  k: number;
}

interface ForkMesh {
  i: number;
  g: THREE.Group;
  /** Where each floe stands across (world x, from the channel's middle when 0), its drift. */
  floes: { m: THREE.Object3D; x: number; drift: number }[];
  lanes: number[];
  /** The ash on the water (its middle across, from and to along), for the smoke rising off it. */
  ash: { x: number; z0: number; z1: number }[];
}

export class BoteLayer {
  readonly group = new THREE.Group();
  private mats: THREE.Material[] = [];
  private geos: THREE.BufferGeometry[] = [];
  private glow: GlowLayer;
  private dome: SkyDome | null = null;
  private terrain: TerrainBuild | null = null;
  private rain: Precip | null = null;
  private sea: THREE.ShaderMaterial | null = null;
  private seaY = -0.62;
  private now = 0;
  private hour: Hour;
  private rnd = looksRng(0xb07e);
  private floor = new Set<string>();
  private hull = new Set<string>();
  /** The floes' cells, drawn as sheets of ice (the board's tiles cut away under them). */
  private ice = new Set<string>();
  private usesIce = false;
  private mat: THREE.MeshStandardMaterial;
  private lights: { p: THREE.Vector3; c: THREE.Color; size: number; a: number; seed: number; flick: number }[] = [];
  private embers: THREE.Vector3[] = [];
  private nextEmber = 0;
  private nextFoam = 0;
  private look = new THREE.Vector3();
  private v3 = new THREE.Vector3();
  private e3 = new THREE.Euler();
  private last: BattleState | null = null;
  private fight: boolean;
  private boatG = new THREE.Group();
  private oars: Oar[] = [];
  // The wake (act 1).
  private flowZ = 0;
  private fork: ForkMesh | null = null;
  private forkY = -30;
  private forkX = 0;
  private drift: THREE.InstancedMesh | null = null;
  private driftAt: THREE.Vector3[] = [];
  private ivarBoat: THREE.Group | null = null;
  private ivarZ = -20;
  private wolfHead: THREE.Group | null = null;
  private stallAt = -10;
  private bankAt = -1;
  // The swell (act 2).
  private crest: THREE.Mesh | null = null;
  private warnAt = -10;
  private waveAt = -10;
  private rope: THREE.InstancedMesh | null = null;
  private heads: THREE.Mesh[] = [];
  // The old howler (act 3).
  private darkK = [0, 0, 0, 0];
  private shadow: THREE.Group | null = null;
  private gray: THREE.Group | null = null;
  private flashAt = -10;
  private nextFlash = 3;
  // Terrón.
  private tBody: THREE.Mesh | null = null;
  private tLegs: THREE.Mesh[] = [];
  private tTrack = { px: 0, py: 0, cx: 0, cy: 0, dist: 0, set: false };
  // The calm's old howler on its floe (a model of its own, standing).
  private calmWolf: THREE.Group | null = null;

  constructor(
    private ctx: OmenCtx,
    private map: MapDef,
    readonly place: string,
    s: BattleState,
  ) {
    this.group.name = `bote-${place}`;
    this.hour = hourOf(place);
    this.fight = place === 'boteWake' || place === 'boteSwell' || place === 'boteElder';
    for (const h of cellsOf(map.radius)) this.floor.add(`${h.q},${h.r}`);
    for (const h of map.rocks ?? []) this.floor.delete(`${h.q},${h.r}`);
    if (place !== 'boteCamp') for (const h of hullCells()) this.hull.add(`${h.q},${h.r}`);
    this.glow = new GlowLayer(ctx.low ? 160 : 320);
    this.mat = this.track(new THREE.MeshStandardMaterial({ vertexColors: true, roughness: 0.84, metalness: 0.04, flatShading: true }));
    const kit = new Kit();
    const seed = s.rules?.senda ? s.seed : BOTE_SCENE_SEED;
    if (place !== 'boteCamp') this.buildBoat(kit, place === 'boteWake');
    this.buildIce(kit);
    if (place === 'boteWake') this.buildWake(kit);
    else if (place === 'boteSwell') this.buildSwell(seed);
    else if (place === 'boteElder') this.buildElder(kit, seed);
    else this.buildScene(kit);
    this.buildTerron();
    const dress = kit.build(this.mat, 'bote-dress');
    if (dress) {
      dress.castShadow = true;
      dress.receiveShadow = true;
      this.geos.push(dress.geometry);
      this.group.add(dress);
    }
    this.group.add(this.boatG);
    // Rain in the storm, spray on the wind by day.
    if (!ctx.crypt) {
      const storm = this.hour === 'storm';
      this.rain = storm
        ? new Precip({ count: ctx.low ? 260 : 520, box: [16, 9, 16], fall: 9, wind: [3.2, 0], sway: 0.2, size: [0.012, 0.32], color: '#b8c8dc', opacity: 0.42 }, 93)
        : new Precip({ count: ctx.low ? 120 : 240, box: [16, 6, 16], fall: 0.6, wind: [3.0, 0.6], sway: 0.6, size: [0.03, 0.08], color: '#eef3f8', opacity: 0.24, round: true }, 92);
      this.group.add(this.rain.mesh);
    }
    this.buildSea();
    this.group.add(this.glow.mesh);
  }

  /* ---------- helpers ---------- */

  private track<T extends THREE.Material | THREE.BufferGeometry>(x: T): T {
    if (x instanceof THREE.Material) this.mats.push(x);
    else this.geos.push(x);
    return x;
  }

  private light(p: THREE.Vector3, color: THREE.Color, size: number, a: number, flick = 0): void {
    this.lights.push({ p: p.clone(), c: color, size, a, seed: this.lights.length * 1.37, flick });
  }

  private at(p: CazaPt, h = 0): THREE.Vector3 {
    return new THREE.Vector3(p.x * K, h, p.y * K);
  }

  private isFloor(x: number, z: number): boolean {
    const h = worldToHex(Math.round(x / K), Math.round(z / K));
    return this.floor.has(`${h.q},${h.r}`);
  }

  private own(geo: THREE.BufferGeometry, color: string, opts: THREE.MeshStandardMaterialParameters = {}): THREE.Mesh {
    const m = new THREE.Mesh(this.track(geo), this.track(new THREE.MeshStandardMaterial({ color, roughness: 0.85, flatShading: true, ...opts })));
    m.castShadow = true;
    return m;
  }

  private buildSea(): void {
    const sea = this.track(seaMat());
    const look = HOURS[this.hour];
    const u = sea.uniforms;
    (u.deep as THREE.IUniform<THREE.Color>).value.set(look.sea[0]);
    (u.shallow as THREE.IUniform<THREE.Color>).value.set(look.sea[1]);
    (u.foamC as THREE.IUniform<THREE.Color>).value.set(look.sea[2]);
    (u.darkC as THREE.IUniform<THREE.Color>).value.set(look.dark);
    (u.rough as THREE.IUniform<number>).value = this.hour === 'storm' ? 0.55 : this.place === 'boteWake' ? 0.3 : 0.1;
    this.sea = sea;
    const m = new THREE.Mesh(this.track(new THREE.PlaneGeometry(300, 300, 1, 1)), sea);
    m.rotation.x = -Math.PI / 2;
    m.position.y = this.seaY;
    m.name = 'bote-sea';
    m.renderOrder = -2;
    this.group.add(m);
  }

  /* ---------- the boat ---------- */

  /**
   * Bera's long boat over the hull's cells: a keel under the water, strakes that flare out from it to the gunwale,
   * planks along the deck, three benches, shields on the gunwale (the clan's red and grey by turns), a tall stem at the
   * bow with the boar's head carved on it, the stern post and the tiller. The oars are their own (they row).
   */
  private buildBoat(kit: Kit, rowing: boolean): void {
    const L = boatLayout();
    const box = new THREE.BoxGeometry(1, 1, 1);
    const z0 = L.bow * K;
    const z1 = L.stern * K;
    const n = 22;
    const half = (t: number): number => 1.12 * S * Math.pow(Math.max(0, Math.sin(Math.PI * t)), 0.55) + 0.12;
    for (let i = 0; i < n; i++) {
      const t = (i + 0.5) / n;
      const z = z0 + (z1 - z0) * t;
      const dz = ((z1 - z0) / n) * 1.06;
      const w = half(t);
      const rise = Math.pow(Math.abs(t - 0.5) * 2, 3) * 0.45;
      // The keel and the bottom, under the water.
      kit.add(box, '#2c241e', place(0, this.seaY - 0.25 + rise * 0.5, z, 0, new THREE.Vector3(w * 0.8, 0.3, dz)));
      // The strakes: three each side, flaring, the top one the gunwale (a little over the deck).
      for (const k of [-1, 1])
        for (let j = 0; j < 3; j++) {
          const yy = this.seaY + 0.05 + j * 0.24 + rise * (0.5 + j * 0.25);
          const xx = k * (w * (0.82 + j * 0.09));
          kit.add(box, j === 2 ? '#3a2f27' : (WOOD[(i + j) % 4] as string), place(xx, yy, z, 0, new THREE.Vector3(0.1, 0.28, dz), 0, -k * (0.35 - j * 0.08)));
        }
      // The gunwale's cap, and the deck's planks between.
      for (const k of [-1, 1]) kit.add(box, '#2f2620', place(k * w * 1.0, 0.12 + rise * 0.6, z, 0, new THREE.Vector3(0.14, 0.08, dz)));
      const planks = Math.max(1, Math.round((w * 1.8) / 0.38));
      for (let p = 0; p < planks; p++) {
        const x = -w * 0.9 + (p + 0.5) * ((w * 1.8) / planks);
        kit.add(box, PLANK[(p + i) % 3] as string, place(x, -0.05 + rise * 0.15, z, 0, new THREE.Vector3((w * 1.8) / planks - 0.03, 0.06, dz)));
      }
    }
    // The benches across, at the oars' rows.
    for (const o of L.oars) if (o.x < 0) kit.add(box, '#6a5643', place(0, 0.12, o.y * K + 0.55, 0, new THREE.Vector3(half(0.5) * 1.7, 0.07, 0.3)));
    // The shields on the gunwale, outside: round, a boss in the middle, red and grey by turns.
    const shield = new THREE.CylinderGeometry(0.33, 0.33, 0.05, 12);
    const boss = new THREE.SphereGeometry(0.08, 8, 6);
    for (let i = 0; i < 9; i++)
      for (const k of [-1, 1]) {
        const t = 0.14 + i * 0.09;
        const z = z0 + (z1 - z0) * t;
        const x = k * (half(t) * 1.02 + 0.04);
        const red = (i + (k > 0 ? 1 : 0)) % 2 === 0;
        kit.add(shield, red ? CLAN : '#5b6168', place(x, 0.2, z, 0, 1, 0, Math.PI / 2));
        kit.add(boss, '#9aa1a8', place(x + k * 0.04, 0.2, z, 0, 1));
      }
    // The stem at the bow, curving up, the boar's head on it: grey stone-like wood, tusks pale.
    const rock = new THREE.DodecahedronGeometry(0.5, 0);
    for (let i = 0; i < 6; i++) {
      const t = i / 5;
      kit.add(box, '#3a2f27', place(0, 0.1 + t * 1.3, z0 - 0.15 - Math.sin(t * 1.3) * 0.65, 0, new THREE.Vector3(0.18, 0.34, 0.2), -t * 0.7, 0));
    }
    const hz = z0 - 0.95;
    kit.add(rock, '#5d544c', place(0, 1.52, hz, 0, new THREE.Vector3(0.42, 0.44, 0.7)));
    kit.add(rock, '#4c443d', place(0, 1.42, hz - 0.42, 0, new THREE.Vector3(0.26, 0.24, 0.36)));
    for (const k of [-1, 1]) {
      kit.add(new THREE.ConeGeometry(0.05, 0.32, 5), '#d0d4d8', place(k * 0.14, 1.36, hz - 0.5, 0, 1, -2.0, k * 0.3));
      kit.add(new THREE.ConeGeometry(0.07, 0.2, 4), '#4c443d', place(k * 0.17, 1.8, hz + 0.05, 0, 1, -0.3, k * 0.4));
      kit.add(new THREE.SphereGeometry(0.035, 6, 5), '#ff4f3a', place(k * 0.17, 1.6, hz - 0.28, 0, 1));
    }
    this.light(new THREE.Vector3(0, 1.6, hz - 0.3), RED, 0.5, 0.28, 0.2);
    // The stern post and the tiller, by Bera.
    for (let i = 0; i < 4; i++) {
      const t = i / 3;
      kit.add(box, '#3a2f27', place(0, 0.1 + t * 0.95, z1 + 0.1 + Math.sin(t * 1.2) * 0.45, 0, new THREE.Vector3(0.16, 0.3, 0.18), t * 0.6, 0));
    }
    kit.add(box, '#5c4b3d', place(0.35, 0.35, z1 - 0.35, 0.5, new THREE.Vector3(0.08, 0.08, 1.1), 0, 0.3));
    // A coil of rope at the bow (the bow rope), a water cask, a spare oar or two along the gunwale.
    const torus = new THREE.TorusGeometry(0.22, 0.06, 5, 12);
    kit.add(torus, '#8b8274', place(0.35, 0.02, z0 + 1.5, 0, 1, Math.PI / 2, 0));
    kit.add(torus, '#8b8274', place(0.35, 0.08, z0 + 1.5, 0.4, 0.85, Math.PI / 2, 0));
    kit.add(new THREE.CylinderGeometry(0.2, 0.22, 0.45, 10), '#4a3d32', place(-0.4, 0.2, z1 - 1.0));
    shield.dispose();
    boss.dispose();
    rock.dispose();
    torus.dispose();
    box.dispose();
    this.buildOars(L.oars, rowing);
  }

  /** The six oars, each its own (they row; a bitten one hangs broken). In the storm and at rest, shipped along the benches. */
  private buildOars(oars: CazaPt[], rowing: boolean): void {
    const shaft = this.track(new THREE.BoxGeometry(0.07, 0.07, 3.2).translate(0, 0, 1.4));
    const blade = this.track(new THREE.BoxGeometry(0.3, 0.04, 0.8));
    const wood = this.track(new THREE.MeshStandardMaterial({ color: '#7a6550', roughness: 0.85, flatShading: true }));
    oars.forEach((p, k) => {
      const g = new THREE.Group();
      const side = p.x > 0 ? 1 : -1;
      g.position.set(side * 1.1 * S * 0.95, 0.16, p.y * K + 0.45);
      const sm = new THREE.Mesh(shaft, wood);
      sm.castShadow = true;
      const bm = new THREE.Mesh(blade, wood);
      bm.position.z = 3.0;
      bm.castShadow = true;
      const pivot = new THREE.Group();
      pivot.rotation.y = side * (Math.PI / 2);
      pivot.add(sm, bm);
      g.add(pivot);
      if (!rowing) {
        // Shipped: in along the gunwale.
        g.position.x = side * 0.75 * S;
        pivot.rotation.y = side > 0 ? 0.12 : Math.PI - 0.12;
        g.position.y = 0.1;
      }
      this.boatG.add(g);
      this.oars.push({ g, blade: bm, side, k });
    });
  }

  /**
   * The ice under the floe cells: sheets with a ragged edge and pale blue sides into the water (engine/ice-sheet.ts),
   * the board's tiles cut away under them (no hex grid on the ice). The tail's shore stays stone.
   */
  private buildIce(kit: Kit): void {
    const shore = this.place === 'boteShore' || this.place === 'boteCamp';
    const geo = new THREE.CylinderGeometry(HR * 1.02, HR * 0.9, 1, 6, 1);
    const ice: { q: number; r: number }[] = [];
    let k = 0;
    for (const key of this.floor) {
      if (this.hull.has(key)) continue;
      const [q, r] = key.split(',').map(Number) as [number, number];
      if (!shore) {
        ice.push({ q, r });
        this.ice.add(key);
        continue;
      }
      // The tail's shore: stone and frozen grass down to the water.
      const p = hexToWorld(q, r);
      kit.add(geo, STONE[k++ % 3] as string, place(p.x * K, (-0.02 + this.seaY - 0.6) / 2, p.y * K, 0, new THREE.Vector3(1, 0.6 - this.seaY, 1)));
    }
    geo.dispose();
    if (!ice.length) return;
    const m = new THREE.Mesh(this.track(iceSheetGeometry(ice, { seed: 0xb07e, top: 0.02, foot: this.seaY - 0.7, topA: this.hour === 'storm' ? '#cfdbe6' : '#e6eef4', topB: this.hour === 'storm' ? '#97adc0' : '#bccfdd' })), this.track(iceMaterial()));
    m.receiveShadow = true;
    m.name = 'bote-ice';
    this.usesIce = true;
    this.group.add(m);
  }

  /* ---------- act 1: the wake ---------- */

  private buildWake(kit: Kit): void {
    void kit;
    // Small floes drifting past on the current (instanced), recycled ahead of the bow as they go astern.
    const g = this.track(new THREE.DodecahedronGeometry(0.5, 0));
    const m = this.track(new THREE.MeshStandardMaterial({ color: '#cfdbe5', roughness: 0.6, flatShading: true }));
    const n = this.ctx.low ? 10 : 16;
    this.drift = new THREE.InstancedMesh(g, m, n);
    this.drift.castShadow = true;
    this.drift.frustumCulled = false;
    for (let i = 0; i < n; i++) {
      const side = i % 2 ? 1 : -1;
      this.driftAt.push(new THREE.Vector3(side * (5 + this.rnd() * 14), 0.4 + this.rnd() * 0.8, -40 + this.rnd() * 80));
    }
    this.group.add(this.drift);
    // Ivar's boat: the same boat with the red prow, rowing in another channel.
    const ik = new Kit();
    this.smallBoat(ik, true);
    const im = ik.build(this.mat, 'bote-ivar');
    if (im) {
      im.castShadow = true;
      this.geos.push(im.geometry);
      this.ivarBoat = new THREE.Group();
      this.ivarBoat.add(im);
      this.ivarBoat.position.set(9, this.seaY + 0.25, -20);
      this.group.add(this.ivarBoat);
    }
    // The Wolf far ahead: a pale grey head and back parting the water.
    const gw = grayWolf({});
    gw.root.scale.setScalar(SCALE.enemy * 2.3);
    this.wolfHead = new THREE.Group();
    this.wolfHead.add(gw.root);
    gw.root.position.y = -0.7;
    this.ownModel(gw.root);
    this.wolfHead.position.set(0, this.seaY, -46);
    this.group.add(this.wolfHead);
  }

  /** A model's meshes tracked for disposal, casting shadows. */
  private ownModel(root: THREE.Object3D): void {
    root.traverse((o) => {
      const m = o as THREE.Mesh;
      if (!m.isMesh) return;
      m.castShadow = true;
      if (m.geometry) this.geos.push(m.geometry);
      const mm = m.material as THREE.Material | THREE.Material[];
      for (const x of Array.isArray(mm) ? mm : [mm]) this.mats.push(x);
    });
  }

  /** A long boat in small (Ivar's, or a scene's): six oars out, shields, a boar's head (red for Ivar). */
  private smallBoat(kit: Kit, red: boolean, sc = 1.6): void {
    const box = new THREE.BoxGeometry(1, 1, 1);
    const n = 9;
    for (let i = 0; i < n; i++) {
      const t = i / (n - 1) - 0.5;
      const w = 0.62 * Math.cos(t * Math.PI * 0.92) + 0.04;
      const rise = Math.pow(Math.abs(t) * 2, 3) * 0.32;
      kit.add(box, '#3b3029', place(0, (-0.1 + rise - 0.18) * sc, t * 4.2 * sc, 0, new THREE.Vector3(w * 1.3 * sc, 0.1 * sc, 0.5 * sc)));
      for (const k of [-1, 1]) kit.add(box, i % 2 ? '#4b3d33' : '#54453a', place(k * w * 0.72 * sc, (rise + 0.02) * sc, t * 4.2 * sc, 0, new THREE.Vector3(0.07 * sc, 0.42 * sc, 0.52 * sc), 0, -k * 0.32));
    }
    for (let i = 0; i < 3; i++)
      for (const k of [-1, 1]) kit.add(box, '#6a5846', place(k * 1.15 * sc, -0.06 * sc, (-1.1 + i * 1.1) * sc, k * 0.15, new THREE.Vector3(1.7 * sc, 0.04 * sc, 0.05 * sc), 0, k * 0.22));
    const shield = new THREE.CylinderGeometry(0.19 * sc, 0.19 * sc, 0.04 * sc, 10);
    for (let i = 0; i < 5; i++) for (const k of [-1, 1]) kit.add(shield, (i + (k > 0 ? 1 : 0)) % 2 ? CLAN : '#5b6168', place(k * 0.66 * sc, 0.28 * sc, (-1.5 + i * 0.75) * sc, 0, 1, 0, Math.PI / 2));
    const rock = new THREE.DodecahedronGeometry(0.5, 0);
    kit.add(rock, red ? '#b23a2a' : '#5d544c', place(0, 0.55 * sc, -2.25 * sc, 0, new THREE.Vector3(0.32 * sc, 0.34 * sc, 0.5 * sc)));
    if (red) kit.add(box, '#b23a2a', place(0, 0.18 * sc, -1.85 * sc, 0, new THREE.Vector3(1.0 * sc, 0.3 * sc, 0.7 * sc)));
    kit.add(box, '#3b3029', place(0, 0.5 * sc, 2.2 * sc, 0, new THREE.Vector3(0.1 * sc, 0.7 * sc, 0.12 * sc), 0.4, 0));
    // Rowers' shapes on the benches.
    for (let i = 0; i < 4; i++) kit.add(rock, '#2c3038', place((i % 2 ? 0.3 : -0.3) * sc, 0.3 * sc, (-1.0 + i * 0.7) * sc, 0, new THREE.Vector3(0.3 * sc, 0.55 * sc, 0.3 * sc)));
    shield.dispose();
    rock.dispose();
    box.dispose();
  }

  /** A floe: a slab of ice with a ragged edge and blocks heaped on it (world units, its top at `top`), kept within `w` across. */
  private floeKit(kit: Kit, x: number, z: number, w: number, d: number, seed: number, top = 0.45, tight = false): void {
    const rnd = looksRng(seed);
    const spread = tight ? 0.22 : 0.6;
    const rock = new THREE.DodecahedronGeometry(0.5, 0);
    const cyl = new THREE.CylinderGeometry(0.5, 0.42, 1, 7, 1);
    for (let i = 0; i < 6; i++) {
      const ox = (rnd() - 0.5) * w * spread;
      const oz = (rnd() - 0.5) * d * 0.7;
      const sx = w * (tight ? 0.62 + rnd() * 0.14 : 0.45 + rnd() * 0.35);
      const sz = d * (0.4 + rnd() * 0.3);
      kit.add(cyl, i % 2 ? (ICE_SIDE[i % 3] as string) : (ICE[i % 4] as string), place(x + ox, this.seaY + (top - this.seaY) / 2 - 0.1, z + oz, rnd() * 3, new THREE.Vector3(sx, top - this.seaY + 0.25, sz)));
    }
    for (let i = 0; i < 5; i++) kit.add(rock, ICE[i % 4] as string, place(x + (rnd() - 0.5) * w * spread, top + 0.15 + rnd() * 0.3, z + (rnd() - 0.5) * d * 0.6, rnd() * 3, new THREE.Vector3(0.6 + rnd() * 0.9, 0.4 + rnd() * 0.6, 0.6 + rnd() * 0.8)));
    rock.dispose();
    cyl.dispose();
  }

  /**
   * A fork's look, built once as it comes in sight: four great floes parting in three channels (each as wide as its
   * `wide`), the current's chevrons on the water by the floes (in: closing; out: opening), the Wolf's broad ash film
   * through one (on far ahead when it goes on; a smear at the mouth only where the pack swam over it), the pack's thin
   * wakes. Laid out round the fork's own middle; the layer moves it.
   */
  private buildFork(f: WakeFork, i: number): ForkMesh {
    const g = new THREE.Group();
    const lanes = f.channels.map((c) => c.lane * BOTE_LANE);
    const widths = f.channels.map((c) => [0, 1.9, 2.7, 3.7][c.wide] as number);
    const floes: ForkMesh['floes'] = [];
    // The floes: one either side of the outer channels, two between.
    const edges: number[] = [];
    edges.push((lanes[0] as number) - (widths[0] as number) / 2 - 3.2);
    for (let k = 0; k < 2; k++) edges.push(((lanes[k] as number) + (widths[k] as number) / 2 + (lanes[k + 1] as number) - (widths[k + 1] as number) / 2) / 2);
    edges.push((lanes[2] as number) + (widths[2] as number) / 2 + 3.2);
    edges.forEach((x, k) => {
      const kit = new Kit();
      const w = k === 0 || k === 3 ? 6 : Math.max(1.6, Math.abs((lanes[k] as number) - (lanes[k - 1] as number)) - ((widths[k] as number) + (widths[k - 1] as number)) / 2);
      this.floeKit(kit, 0, 0, w, 7.5, 500 + i * 10 + k, 0.5, true);
      const m = kit.build(this.mat, `bote-fork-${k}`);
      const o = m ?? new THREE.Object3D();
      if (m) {
        m.castShadow = true;
        m.receiveShadow = true;
        this.geos.push(m.geometry);
      }
      o.position.x = x;
      g.add(o);
      // Which way it drifts: toward a closing channel next to it, away from an opening one.
      let drift = 0;
      const left = f.channels[k - 1];
      const right = f.channels[k];
      if (left) drift += left.drift < 0 ? -1 : left.drift > 0 ? 1 : 0;
      if (right) drift += right.drift < 0 ? 1 : right.drift > 0 ? -1 : 0;
      floes.push({ m: o, x, drift: Math.sign(drift) });
    });
    // (One draw for the ash films, one for the pack's lines, one instanced for the chevrons: a fork costs six draws.)
    const films: THREE.BufferGeometry[] = [];
    const lines: THREE.BufferGeometry[] = [];
    const chevs: THREE.Matrix4[] = [];
    const film = (x: number, z0: number, z1: number, w: number): void => {
      films.push(new THREE.PlaneGeometry(w, z1 - z0).rotateX(-Math.PI / 2).translate(x, this.seaY + 0.03, (z0 + z1) / 2));
    };
    const ash: ForkMesh['ash'] = [];
    const q = new THREE.Quaternion();
    const one = new THREE.Vector3(1, 1, 1);
    f.channels.forEach((c, k) => {
      const x = lanes[k] as number;
      const w = widths[k] as number;
      // The pack's wakes: thin white lines, crossing.
      for (let j = 0; j < c.pack; j++) {
        const off = ((j % 2 ? 1 : -1) * (j + 1) * w) / (c.pack * 2.6);
        lines.push(new THREE.PlaneGeometry(0.17, 8).rotateX(-Math.PI / 2).rotateY((j % 2 ? 1 : -1) * 0.08).translate(x + off, this.seaY + 0.04, -1 - j * 0.6));
      }
      // The Wolf's ash: a broad grey film on through the channel and far ahead; a smear only at a mouth.
      if (c.ash) {
        film(x, -40, 5, Math.min(w * 0.85, 2.3));
        ash.push({ x, z0: -40, z1: 5 });
      } else if (c.smear) {
        film(x, 1.5, 5, Math.min(w * 0.85, 2.0));
        ash.push({ x, z0: 1.5, z1: 5 });
      }
      // The current: chevrons on the water by each floe edge of the channel, pointing in (closing) or out (opening).
      if (c.drift !== 0)
        for (const sd of [-1, 1])
          for (let j = 0; j < 3; j++) {
            const out = sd * (c.drift > 0 ? 1 : -1);
            q.setFromEuler(this.e3.set(0, out > 0 ? 0 : Math.PI, 0));
            chevs.push(new THREE.Matrix4().compose(this.v3.set(x + sd * (w / 2 + 0.55), this.seaY + 0.05, -2.2 + j * 2.2), q, one));
          }
    });
    const merged = (gs: THREE.BufferGeometry[], color: string, opacity: number, order: number): void => {
      if (!gs.length) return;
      const geo = this.track(mergeGeometries(gs) as THREE.BufferGeometry);
      for (const x of gs) x.dispose();
      const m = new THREE.Mesh(geo, this.track(new THREE.MeshBasicMaterial({ color, transparent: true, opacity, depthWrite: false, fog: false })));
      m.renderOrder = order;
      g.add(m);
    };
    merged(films, '#2a2c31', 0.84, 1);
    merged(lines, '#f4f8fb', 0.9, 2);
    if (chevs.length) {
      const chevron = this.track(new THREE.ConeGeometry(0.22, 0.7, 3).rotateZ(-Math.PI / 2).rotateX(-Math.PI / 2));
      const cm = new THREE.InstancedMesh(chevron, this.track(new THREE.MeshBasicMaterial({ color: '#eef3f8', transparent: true, opacity: 0.85, depthWrite: false })), chevs.length);
      chevs.forEach((m, k) => cm.setMatrixAt(k, m));
      cm.renderOrder = 3;
      cm.frustumCulled = false;
      cm.userData.chev = 1;
      g.add(cm);
    }
    this.group.add(g);
    return { i, g, floes, lanes, ash };
  }

  /* ---------- act 2: the swell ---------- */

  private buildSwell(seed: number): void {
    const L = swellLayout(seed);
    const kit = new Kit();
    for (const [i, f] of L.floes.entries()) this.floeKit(kit, f.x * K, f.y * K, 2.6 * S, 2.2 * S, 600 + i, 0.05);
    const m = kit.build(this.mat, 'bote-swell-floes');
    if (m) {
      m.castShadow = true;
      m.receiveShadow = true;
      this.geos.push(m.geometry);
      this.group.add(m);
    }
    // The wave's crest: a long ridge of water with its foam, coming at the bow.
    const crest = this.own(new THREE.CylinderGeometry(0.9, 1.4, 34, 14, 1), '#6b8aa0', { roughness: 0.35, metalness: 0.1, emissive: '#1d2c38', emissiveIntensity: 0.6 });
    crest.rotation.z = Math.PI / 2;
    // Its foam along the top, white.
    const foam = this.own(new THREE.CylinderGeometry(0.35, 0.5, 33, 10, 1), '#e8eef4', { roughness: 0.6, emissive: '#5a6672', emissiveIntensity: 0.5 });
    foam.position.y = 0.95;
    crest.add(foam);
    crest.visible = false;
    this.crest = crest;
    this.group.add(crest);
    this.buildRope();
    this.buildHeads();
  }

  private buildRope(): void {
    const geo = this.track(new THREE.BoxGeometry(0.06, 0.06, 1));
    const mat = this.track(new THREE.MeshStandardMaterial({ color: '#8b8274', roughness: 0.95 }));
    this.rope = new THREE.InstancedMesh(geo, mat, 14);
    this.rope.count = 0;
    this.rope.frustumCulled = false;
    this.group.add(this.rope);
  }

  /** The heads of who the water took, bobbing in the sea (four at most). */
  private buildHeads(): void {
    const geo = this.track(new THREE.SphereGeometry(0.2, 8, 6));
    const mat = this.track(new THREE.MeshStandardMaterial({ color: '#c9a58e', roughness: 0.8 }));
    for (let i = 0; i < 4; i++) {
      const m = new THREE.Mesh(geo, mat);
      m.visible = false;
      this.heads.push(m);
      this.group.add(m);
    }
  }

  /* ---------- act 3: the old howler ---------- */

  private buildElder(kit: Kit, seed: number): void {
    const L = elderLayout(seed);
    // Floes heaped at their middles and tips (the cells are the board's; the heaps say where the ice is thick).
    L.floes.forEach((f, i) => this.floeKit(kit, f.x * K, f.y * K, 2.2 * S, 1.8 * S, 700 + i, 0.02));
    // The leap's shadow: a dark disc with a red rim, under where it will land.
    const sh = new THREE.Group();
    const disc = new THREE.Mesh(this.track(new THREE.CircleGeometry(1, 28).rotateX(-Math.PI / 2)), this.track(new THREE.MeshBasicMaterial({ color: '#05070a', transparent: true, opacity: 0.55, depthWrite: false })));
    const rim = new THREE.Mesh(this.track(new THREE.RingGeometry(0.9, 1, 32).rotateX(-Math.PI / 2)), this.track(new THREE.MeshBasicMaterial({ color: '#ff4f3a', transparent: true, opacity: 0.9, depthWrite: false })));
    disc.renderOrder = 4;
    rim.renderOrder = 5;
    sh.add(disc, rim);
    sh.visible = false;
    this.shadow = sh;
    this.group.add(sh);
    // The Grey Wolf circling far off, shown when the lightning comes.
    const gw = grayWolf({});
    gw.root.scale.setScalar(SCALE.enemy * 2.3);
    gw.root.position.y = -0.8;
    this.ownModel(gw.root);
    this.gray = new THREE.Group();
    this.gray.add(gw.root);
    this.group.add(this.gray);
  }

  /* ---------- the scenes ---------- */

  private buildScene(kit: Kit): void {
    const box = new THREE.BoxGeometry(1, 1, 1);
    const rock = new THREE.DodecahedronGeometry(0.5, 0);
    if (this.place === 'boteShore' || this.place === 'boteCamp') {
      // The camp coming down on the shore: carts loaded, hides tied in bundles, a tent half struck, the herd in a line
      // going off along the flank.
      const rnd = looksRng(0x5ca);
      const wheel = new THREE.CylinderGeometry(0.32, 0.32, 0.1, 10);
      for (let i = 0; i < 4; i++) {
        const x = -6 + i * 4.2;
        const z = 9.5 + (i % 2) * 1.6;
        kit.add(box, WOOD[i % 4] as string, place(x, 0.55, z, 0.2 * i, new THREE.Vector3(1.6, 0.35, 0.9)));
        for (const k of [-1, 1]) kit.add(wheel, '#3a2f27', place(x + k * 0.6, 0.32, z + 0.5, 0.2 * i, 1, Math.PI / 2, 0));
        for (let j = 0; j < 3; j++) kit.add(rock, ['#6b5a4b', '#7d6c5c', '#5a4b3e'][j] as string, place(x + (j - 1) * 0.45, 0.95, z, rnd() * 3, new THREE.Vector3(0.55, 0.45, 0.7)));
      }
      const cone = new THREE.ConeGeometry(1.4, 2.2, 7, 1, true);
      kit.add(cone, '#6a5b4d', place(5, 0.9, 12.5, 0.3, 1, 0.5, 0.2));
      cone.dispose();
      // The herd in a line toward the flank (stone boars, small: the herd's body).
      const body = boarBody(17);
      for (let i = 0; i < 9; i++) kit.add(body, '#c3cfbf', place(9 + i * 1.6, 0.0, 9 + i * 0.7, -1.2, SCALE.hero * 1.0));
      body.dispose();
      // Frozen grass on the stone.
      const tuft = new THREE.ConeGeometry(0.06, 0.3, 3);
      for (let i = 0; i < 120; i++) {
        const x = (rnd() * 2 - 1) * 14;
        const z = 6.5 + rnd() * 9;
        if (!this.isFloor(x, z)) continue;
        kit.add(tuft, ['#56634f', '#4c5946', '#606d58'][i % 3] as string, place(x, 0.13, z, i, new THREE.Vector3(1, 0.8 + rnd() * 0.6, 1)));
      }
      tuft.dispose();
      wheel.dispose();
      // The cinematic: the boat out on the water, going.
      if (this.place === 'boteCamp') {
        this.smallBoat(kit, false, 1.7);
      }
    } else if (this.place === 'boteCalm') {
      // The floe with the old howler on it, looking at the boat; the sea gone still.
      this.floeKit(kit, 4.5 * K * 1000, -4.8 * K * 1000, 2.4 * S, 2.2 * S, 801, 0.02);
      const eh = elderHowler({});
      eh.root.scale.setScalar(SCALE.enemy * 1.95);
      eh.root.position.set(4.5 * S, 0.02, -4.8 * S);
      eh.root.rotation.y = Math.atan2(-4.5, 4.8);
      this.ownModel(eh.root);
      this.calmWolf = eh.root;
      this.group.add(eh.root);
      for (let i = 0; i < 6; i++) this.floeKit(kit, (i % 2 ? -1 : 1) * (8 + i * 2) * S, (-8 - i * 3) * S, 1.6 * S, 1.4 * S, 820 + i, 0.2);
    } else if (this.place === 'boteFire') {
      // The sail rigged low as a tent over the middle of the boat, on two oars; a fire-pot on a flat stone.
      const sail = new THREE.PlaneGeometry(1, 1);
      for (const k of [-1, 1]) kit.add(sail, k > 0 ? '#bfc4c6' : '#b3b8bb', place(k * 0.9, 1.2, 0, 0, new THREE.Vector3(2.1, 1, 4.6), -Math.PI / 2, k * 0.95));
      for (let i = 0; i < 5; i++) kit.add(box, i % 2 ? CLAN : '#a9aeb1', place(0, 1.92, -2 + i, 0, new THREE.Vector3(0.05, 0.05, 0.9)));
      for (const z of [-2.3, 2.3]) kit.add(box, '#6a5846', place(0, 1.0, z, 0, new THREE.Vector3(0.08, 2.0, 0.08)));
      sail.dispose();
      kit.add(rock, '#5d646c', place(0, 0.04, 0.2, 0, new THREE.Vector3(0.8, 0.12, 0.8)));
      const p = new THREE.Vector3(0, 0.12, 0.2);
      kit.add(new THREE.CylinderGeometry(0.26, 0.2, 0.22, 10), '#3a3230', place(p.x, p.y + 0.1, p.z));
      kit.add(new THREE.CylinderGeometry(0.2, 0.2, 0.04, 10), '#ff7a3a', place(p.x, p.y + 0.22, p.z));
      const f = new THREE.Vector3(p.x, p.y + 0.45, p.z);
      this.light(f, FIRE_CORE, 0.8, 0.95, 1);
      this.light(f.clone().setY(f.y + 0.2), FIRE, 2.0, 0.75, 1);
      this.light(f.clone().setY(p.y + 0.1), FIRE, 4.0, 0.22, 0.5);
      this.embers.push(f);
    } else if (this.place === 'boteDawn') {
      // The White Island to the north: high rock under ice, still; Ivar's boat run aground on its shore.
      const rnd = looksRng(0x1514);
      for (let i = 0; i < 26; i++) {
        const a = (i / 26) * Math.PI * 2;
        const rr = 10 + rnd() * 7;
        const x = Math.cos(a) * rr;
        const z = -38 + Math.sin(a) * rr * 0.6;
        const tall = 3 + rnd() * 9 + (rr < 12 ? 6 : 0);
        kit.add(rock, i % 3 ? '#e4ebf1' : '#9aa6b1', place(x, this.seaY + tall * 0.45, z, rnd() * 3, new THREE.Vector3(5 + rnd() * 4, tall, 5 + rnd() * 3)));
      }
      for (let i = 0; i < 6; i++) kit.add(rock, '#f2f6f9', place((rnd() - 0.5) * 8, 12 + rnd() * 6, -40 + (rnd() - 0.5) * 6, rnd() * 3, new THREE.Vector3(5, 5 + rnd() * 4, 4)));
      // The beach of ice at its foot, and the red-prowed boat on it.
      this.floeKit(kit, -6, -26, 12, 4, 902, -0.2);
      const ik = new Kit();
      this.smallBoat(ik, true, 1.5);
      const im = ik.build(this.mat, 'bote-ivar-dawn');
      if (im) {
        this.geos.push(im.geometry);
        im.position.set(-7, -0.05, -25.5);
        im.rotation.set(0.12, 1.2, 0.1);
        this.group.add(im);
      }
    }
    box.dispose();
    rock.dispose();
  }

  /* ---------- Terrón ---------- */

  private buildTerron(): void {
    const mat = this.track(new THREE.MeshStandardMaterial({ color: TERRON, roughness: 0.88, flatShading: true }));
    this.tBody = new THREE.Mesh(this.track(boarBody(13)), mat);
    this.tBody.castShadow = true;
    this.tBody.visible = false;
    this.group.add(this.tBody);
    const leg = this.track(boarLeg());
    for (let i = 0; i < 4; i++) {
      const m = new THREE.Mesh(leg, mat);
      m.visible = false;
      this.tLegs.push(m);
      this.group.add(m);
    }
  }

  private drawTerron(s: BattleState | null, alpha: number): void {
    const body = this.tBody;
    if (!body) return;
    const tn = s?.senda?.npcs.find((x) => x.id === 'terron' && x.hp > 0);
    let x: number;
    let y: number;
    let facing: number;
    let moving = false;
    let lie = false;
    if (tn) {
      const t = this.tTrack;
      x = t.set ? t.px + (t.cx - t.px) * alpha : tn.x;
      y = t.set ? t.py + (t.cy - t.py) * alpha : tn.y;
      facing = tn.facing;
      moving = tn.moving;
    } else {
      const fig = s?.heroes.find((h) => h.face === 'terron');
      const p = fig ? this.ctx.pos(fig.id) : undefined;
      if (!fig || !p) {
        body.visible = false;
        for (const l of this.tLegs) l.visible = false;
        return;
      }
      x = p.x / K;
      y = p.z / K;
      facing = fig.facing ?? 0;
      lie = this.place === 'boteFire' || this.place === 'boteDawn';
    }
    const sc = SCALE.hero * 1.2;
    const g = this.ctx.at(x, y, lie ? -0.08 : 0);
    const stride = moving ? this.tTrack.dist * 9.5 : 0;
    const bob = moving ? Math.abs(Math.sin(stride)) * 0.025 : Math.max(0, Math.sin(this.now * 0.9)) * 0.01;
    const yaw = facingToRotY(facing);
    this.e3.set(0, yaw, Math.sin(stride) * 0.03, 'YXZ');
    body.position.set(g.x, g.y + bob, g.z);
    body.quaternion.setFromEuler(this.e3);
    body.scale.setScalar(sc);
    body.visible = true;
    const cy = Math.cos(yaw);
    const sy = Math.sin(yaw);
    this.tLegs.forEach((l, i) => {
      const [lx, lz] = BOAR_LEGS[i] as [number, number];
      const ph = i === 0 || i === 3 ? 1 : -1;
      this.e3.set(lie ? 1.2 * ph : Math.sin(stride) * 0.5 * ph, yaw, 0, 'YXZ');
      l.quaternion.setFromEuler(this.e3);
      l.position.set(g.x + (lx * cy + lz * sy) * sc, g.y + BOAR_HIP_Y * sc + bob, g.z + (-lx * sy + lz * cy) * sc);
      l.scale.setScalar(sc);
      l.visible = true;
    });
  }

  /* ---------- the board, repainted ---------- */

  apply(terrain: TerrainBuild): void {
    this.terrain = terrain;
    const c = new THREE.Color();
    const c2 = new THREE.Color();
    const col = terrain.mesh.geometry.getAttribute('color') as THREE.BufferAttribute | undefined;
    const pos = terrain.mesh.geometry.getAttribute('position') as THREE.BufferAttribute | undefined;
    smoothTiles(terrain.mesh.geometry, terrain.heights, this.map.radius, terrain.ground);
    const shore = this.place === 'boteShore' || this.place === 'boteCamp';
    if (col && pos) {
      terrain.mesh.updateMatrixWorld(true);
      const v = new THREE.Vector3();
      for (let i = 0; i < col.count; i++) {
        v.fromBufferAttribute(pos, i).applyMatrix4(terrain.mesh.matrixWorld);
        const k = 0.5 + 0.25 * Math.sin(v.x * 0.41 + v.z * 0.23) * Math.cos(v.z * 0.33 - v.x * 0.19) + 0.25 * Math.sin(v.x * 1.1 + 1.3) * Math.sin(v.z * 0.9 + 0.4);
        if (shore) c.set('#5e6670').lerp(c2.set('#4f5a4c'), Math.max(0, Math.min(1, k)));
        else {
          // Ice: white with pale blue in the hollows and cracks of darker blue.
          c.set(this.hour === 'storm' ? '#c7d4df' : '#e3ebf1').lerp(c2.set(this.hour === 'storm' ? '#93a9bc' : '#b9cad7'), Math.max(0, Math.min(1, k)));
          const cr = Math.abs(Math.sin(v.x * 2.3 + v.z * 1.7) * Math.sin(v.z * 2.9 - v.x * 1.1));
          if (cr < 0.05) c.lerp(c2.set('#6f8aa1'), 0.6);
        }
        col.setXYZ(i, c.r, c.g, c.b);
      }
      col.needsUpdate = true;
    }
    terrain.decor.traverse((o) => {
      if (o.name === 'roads' || o.name === 'outskirts-ground') o.visible = false;
      if ((o as THREE.InstancedMesh).isInstancedMesh) o.visible = false;
    });
    this.carve(terrain);
    // (No fog bank at the board's edge out at sea: it laid a pale band over the fork ahead and washed out its ash.)
    if (terrain.fog) terrain.fog.group.visible = false;
    this.dome = new SkyDome();
    this.group.add(this.dome.mesh);
    this.paint();
  }

  /** The sea everywhere but the ice and the shore: the board's ground cut away there, and under the boat's deck. */
  private carve(terrain: TerrainBuild): void {
    const mesh = terrain.mesh;
    const geo = mesh.geometry;
    const pos = geo.getAttribute('position') as THREE.BufferAttribute | undefined;
    if (!pos) return;
    mesh.updateMatrixWorld(true);
    const mw = mesh.matrixWorld;
    const v = new THREE.Vector3();
    const idx = geo.index;
    const tri = (i: number): number => (idx ? idx.getX(i) : i);
    const n = idx ? idx.count : pos.count;
    const keep: number[] = [];
    for (let i = 0; i < n; i += 3) {
      let cx = 0;
      let cz = 0;
      for (let k = 0; k < 3; k++) {
        v.fromBufferAttribute(pos, tri(i + k)).applyMatrix4(mw);
        cx += v.x;
        cz += v.z;
      }
      const h = worldToHex(Math.round(cx / 3 / K), Math.round(cz / 3 / K));
      const key = `${h.q},${h.r}`;
      const out = !this.floor.has(key) || this.hull.has(key) || this.ice.has(key);
      if (idx) {
        if (!out) keep.push(tri(i), tri(i + 1), tri(i + 2));
      } else if (out) {
        const x = pos.getX(i);
        const y = pos.getY(i);
        const z = pos.getZ(i);
        pos.setXYZ(i + 1, x, y, z);
        pos.setXYZ(i + 2, x, y, z);
      }
    }
    if (idx) geo.setIndex(keep);
    pos.needsUpdate = true;
    geo.computeBoundingSphere();
  }

  paint(): void {
    const look = HOURS[this.hour];
    const scene = this.ctx.scene;
    scene.background = new THREE.Color(look.bg);
    if (scene.fog instanceof THREE.Fog) {
      scene.fog.color.set(look.fog[0]);
      scene.fog.near = look.fog[1];
      scene.fog.far = look.fog[2];
    }
    const { hemi, key, rim } = this.ctx;
    if (hemi) {
      hemi.color.set(look.hemi[0]);
      hemi.groundColor.set(look.hemi[1]);
      hemi.intensity = look.hemi[2];
    }
    if (key) {
      key.color.set(look.key[0]);
      key.intensity = look.key[1];
    }
    if (rim) {
      rim.color.set(look.rim[0]);
      rim.intensity = look.rim[1];
    }
    this.dome?.set(look.sky[0], look.sky[1], look.sky[2]);
    if (this.dome) this.dome.u.uSunDir.value.set(this.hour === 'dusk' ? 0.2 : -0.4, this.hour === 'dusk' ? 0.08 : this.hour === 'dawn' ? 0.15 : 0.35, -0.9);
    this.terrain?.fog?.tint({ low: look.fog[0], mid: look.fog[0], far: look.bg, alpha: 1 });
  }

  /* ---------- each tick and frame ---------- */

  tick(s: BattleState): void {
    this.last = s;
    const t = s.senda?.npcs.find((n) => n.id === 'terron');
    if (t) {
      const k = this.tTrack;
      if (!k.set) Object.assign(k, { px: t.x, py: t.y, cx: t.x, cy: t.y, dist: 0, set: true });
      else {
        k.dist += Math.hypot(t.x - k.cx, t.y - k.cy) * K;
        k.px = k.cx;
        k.py = k.cy;
        k.cx = t.x;
        k.cy = t.y;
      }
    }
    for (const e of s._events ?? []) {
      if (e.t !== 'senda') continue;
      const p = this.ctx.at(e.x, e.y, 0.1);
      switch (e.kind) {
        case 'bOar': {
          if ((e.n ?? 0) >= 100) break;
          this.ctx.vfx.burst(p.setY(0.4), { n: 12, color: '#8a7158', speed: 0.05, up: 0.08, size: 0.09, life: 22, gravity: 0.008 });
          break;
        }
        case 'bStall':
          this.stallAt = this.now;
          this.ctx.shake(0.3);
          break;
        case 'bGnaw':
          this.ctx.vfx.burst(p.setY(0.2), { n: 4 + (e.n ?? 1) * 2, color: '#6a5846', speed: 0.03, up: 0.05, size: 0.07, life: 14, gravity: 0.006 });
          break;
        case 'bTake':
          if (e.n !== undefined && e.n % 10 === 1) this.ctx.vfx.ring(new THREE.Vector3(0, 0.2, -4.5 * S), 0.5, 5, '#e8eef8', 18, 0.7);
          break;
        case 'bBank':
          this.bankAt = this.now;
          break;
        case 'bWaveWarn':
          this.warnAt = this.now;
          break;
        case 'bWave':
          this.waveAt = this.now;
          this.ctx.shake(0.32);
          for (let i = 0; i < 8; i++) this.splash(new THREE.Vector3((this.rnd() - 0.5) * 3, 0.4, (-4 + i * 1.1) * S * 0.9), 0.7);
          break;
        case 'bOverboard':
        case 'bSwept':
          this.splash(new THREE.Vector3(p.x, this.seaY, p.z), 0.9);
          break;
        case 'bHaul':
          this.ctx.vfx.ring(p.clone().setY(0.2), 0.3, 2.2, '#eef3f8', 14, 0.8);
          break;
        case 'bFloeLeap':
          this.splash(new THREE.Vector3(p.x, this.seaY, p.z), 0.5);
          break;
        case 'bLook':
          this.ctx.vfx.ring(p.clone().setY(0.15), 2.4, 0.4, '#ff4f3a', 20, 0.95);
          break;
        case 'bDive':
          this.splash(new THREE.Vector3(p.x, this.seaY, p.z), 1.3);
          break;
        case 'bRise':
          this.splash(new THREE.Vector3(p.x, this.seaY, p.z), 1.0);
          this.ctx.vfx.burst(p.clone().setY(0.5), { n: 14, color: '#2c3038', speed: 0.03, up: 0.06, size: 0.2, life: 26, smoke: true });
          break;
        case 'bCut':
          this.ctx.vfx.ring(p.clone().setY(0.3), 0.3, 3.2, '#ff8a6e', 16, 0.95);
          this.ctx.shake(0.12);
          break;
        case 'bLeap':
          this.ctx.vfx.ring(p.clone().setY(0.12), 0.3, 2.6, '#e8eef8', 16, 0.8);
          this.ctx.vfx.burst(p.clone().setY(0.3), { n: 16, color: '#d6e0ea', speed: 0.06, up: 0.08, size: 0.12, life: 18, gravity: 0.008 });
          this.ctx.shake(0.2);
          break;
        case 'bRope':
          this.ctx.vfx.ring(p.clone().setY(0.15), 0.3, 1.4, '#eef3f8', 10, 0.7);
          break;
      }
    }
  }

  /** A column of foam out of the sea. */
  private splash(p: THREE.Vector3, big: number): void {
    this.ctx.vfx.burst(p.clone().setY(this.seaY + 0.1), { n: Math.round(22 * big), color: '#eef3f8', speed: 0.05 * big, up: 0.15 * big, size: 0.2 * big, life: 32, gravity: 0.008, spread: 0.6 });
    this.ctx.vfx.ring(p.clone().setY(this.seaY + 0.05), 0.4, 3 * big, '#e6eef6', 18, 0.8);
  }

  draw(s: BattleState): void {
    this.last = s;
  }

  /** The act 1 world, each frame: the sea's flow, the drift floes, the fork in sight sliding past, Ivar, the Wolf. */
  private frameWake(now: number, dt: number): void {
    const s = this.last;
    const r = s?.rules?.senda?.bote?.wake;
    const w = s?.senda?.bote?.wake;
    if (!s || !r || !w) return;
    const rowing = w.oars.filter((o) => o.mode === 0).length;
    const stalled = w.stage === 1 && w.how[w.how.length - 1] === 3;
    const speed = w.done >= 0 ? 1.2 : stalled ? 0 : 1.4 + rowing * 0.55;
    this.flowZ += speed * dt;
    if (this.sea) (this.sea.uniforms.flow as THREE.IUniform<number>).value = this.flowZ;
    // The oars: rowing, stroke after stroke; a bitten one hangs broken, a spare coming out.
    for (const o of this.oars) {
      const st = w.oars[o.k];
      const pivot = o.g.children[0] as THREE.Object3D;
      if (!st) continue;
      if (st.mode === 0 && !stalled) {
        const ph = now * 2.3 + (o.k % 2) * 0.15;
        pivot.rotation.y = o.side * (Math.PI / 2) + Math.sin(ph) * 0.45 * o.side;
        o.g.rotation.z = (Math.cos(ph) * 0.12 - 0.1) * o.side;
        o.blade.visible = true;
        pivot.scale.z = 1;
      } else if (st.mode === 2) {
        pivot.rotation.y = o.side * (Math.PI / 2) * 0.4;
        o.g.rotation.z = -0.5 * o.side;
        o.blade.visible = true;
        pivot.scale.z = 1;
      } else {
        // Broken: half a shaft, no blade, hanging.
        pivot.rotation.y = o.side * (Math.PI / 2) * 0.9;
        o.g.rotation.z = 0.55 * o.side;
        o.blade.visible = false;
        pivot.scale.z = 0.5;
      }
    }
    // The drift floes go astern; ahead again once past.
    if (this.drift) {
      const m = new THREE.Matrix4();
      const q = new THREE.Quaternion();
      this.driftAt.forEach((p, i) => {
        p.z += speed * dt;
        if (p.z > 40) {
          p.z = -45 - this.rnd() * 10;
          p.x = (i % 2 ? 1 : -1) * (6 + this.rnd() * 14);
        }
        q.setFromEuler(this.e3.set(0, i * 1.3 + p.z * 0.02, 0));
        m.compose(this.v3.set(p.x, this.seaY + 0.1, p.z), q, new THREE.Vector3(p.y * 1.4, p.y * 0.6, p.y * 1.2));
        this.drift!.setMatrixAt(i, m);
      });
      this.drift.instanceMatrix.needsUpdate = true;
    }
    // The fork in sight: built as it comes, coming at the bow, then past the boat as it goes through the channel taken.
    const f = wakeFork(s);
    const idx = w.fork;
    const fr = r.forks[idx];
    if (fr && (f || w.stage === 1) && (!this.fork || this.fork.i !== idx)) {
      if (this.fork) this.fork.g.removeFromParent();
      this.fork = this.buildFork(fr, idx);
      this.forkY = -44;
      this.forkX = 0;
    }
    if (this.fork && fr) {
      let ty: number;
      let tx = 0;
      if (w.stage === 0 && w.fork === this.fork.i) ty = boteForkZ(s) ?? this.forkY; else if (w.fork === this.fork.i) {
        const took = w.took[this.fork.i] ?? fr.wide;
        tx = -(this.fork.lanes[took] as number);
        const k = Math.max(0, Math.min(1, 1 - (w.until - s.waveT) / Math.max(1, stalled ? r.stall : r.pass)));
        ty = stalled ? -10 + k * 5 : -10 + k * 32;
      } else ty = this.forkY + speed * dt;
      this.forkY += (ty - this.forkY) * Math.min(1, dt * 3);
      this.forkX += (tx - this.forkX) * Math.min(1, dt * 1.6);
      this.fork.g.position.set(this.forkX, 0, this.forkY);
      // The current: the chevrons sliding the way it goes; the floes edging in on a closing channel.
      const t = (now * 0.8) % 1;
      // (The chevrons share one material: one opacity for them all, breathing the way the current goes.)
      for (const o of this.fork.g.children) {
        if (o.userData.chev === undefined) continue;
        ((o as THREE.Mesh).material as THREE.MeshBasicMaterial).opacity = 0.35 + 0.55 * Math.sin(t * Math.PI);
        break;
      }
      // Grey smoke lifting off the Wolf's ash (and off a smear): it reads from far, on a phone.
      if (Math.floor(now * 6) !== Math.floor((now - dt) * 6))
        for (const a of this.fork.ash) {
          const z = this.forkY + a.z0 + this.rnd() * (a.z1 - a.z0);
          if (z < -30 || z > 8) continue;
          this.ctx.vfx.burst(new THREE.Vector3(this.forkX + a.x + (this.rnd() - 0.5) * 0.8, this.seaY + 0.25, z), { n: 2, color: '#5a5e66', speed: 0.01, up: 0.03, size: 0.5, life: 34, smoke: true, spread: 0.3 });
        }
      for (const fl of this.fork.floes) fl.m.position.x = fl.x + fl.drift * Math.min(1.6, (this.forkY + 44) * 0.03) * (stalled ? 2.2 : 1);
      if (this.forkY > 40) {
        this.fork.g.removeFromParent();
        this.fork = null;
      }
    }
    if (stalled && now - this.stallAt < 6 && Math.floor(now * 4) !== Math.floor((now - dt) * 4))
      for (const k of [-1, 1]) this.ctx.vfx.burst(new THREE.Vector3(k * 1.6 * S, 0.2, (this.rnd() - 0.5) * 6), { n: 6, color: '#dfe8ef', speed: 0.05, up: 0.08, size: 0.14, life: 18, gravity: 0.008 });
    // Ivar's boat: in the widest channel at a fork, else off to the east; ahead or behind as the distances stand.
    if (this.ivarBoat) {
      const ahead = (w.ivar - w.dist) / 1000;
      const tz = Math.max(-40, Math.min(26, -6 - ahead * 5));
      this.ivarZ += (tz - this.ivarZ) * Math.min(1, dt * 0.6);
      const lane = fr && this.fork && this.fork.i === w.fork ? (this.fork.lanes[fr.wide] as number) + this.forkX : 11;
      const tx = w.done >= 0 ? 12 : Math.abs(lane) < 4 ? (lane < 0 ? -12 : 12) : lane;
      this.ivarBoat.position.x += (tx - this.ivarBoat.position.x) * Math.min(1, dt * 0.8);
      this.ivarBoat.position.z = this.ivarZ;
      this.ivarBoat.position.y = this.seaY + 0.25 + Math.sin(now * 1.3) * 0.06;
      this.ivarBoat.rotation.z = Math.sin(now * 1.1) * 0.03;
    }
    // The Wolf ahead, farther with the bar emptier; the bank of great floes at the end.
    if (this.wolfHead) {
      const z = w.done >= 0 ? -26 : -30 - (1 - w.dist / 10000) * 22;
      this.wolfHead.position.z += (z - this.wolfHead.position.z) * Math.min(1, dt * 0.7);
      this.wolfHead.position.y = this.seaY + Math.sin(now * 1.8) * 0.08;
      this.wolfHead.position.x = Math.sin(now * 0.3) * 3;
      if (Math.floor(now * 3) !== Math.floor((now - dt) * 3)) this.ctx.vfx.burst(this.wolfHead.position.clone().setY(this.seaY + 0.1), { n: 3, color: '#8b9097', speed: 0.02, up: 0.02, size: 0.4, life: 40, smoke: true, spread: 0.6 });
    }
  }

  /** The act 2 world: the wave's crest coming at the bow, the bow rope while the hero swings, the heads in the sea. */
  private frameSwell(now: number): void {
    const s = this.last;
    const r = s?.rules?.senda?.bote?.swell;
    const w = s?.senda?.bote?.swell;
    if (!s || !r || !w) return;
    const L = boatLayout();
    if (this.crest) {
      const tw = now - this.warnAt;
      const tb = now - this.waveAt;
      if (tw >= 0 && tw < 3.6 && this.warnAt > this.waveAt - 0.01) {
        const k = Math.min(1, tw / 3);
        this.crest.visible = true;
        this.crest.position.set(0, this.seaY - 0.6 + k * 1.3, L.bow * K - 13 + k * 12);
        this.crest.scale.set(0.6 + k * 0.9, 1, 0.6 + k * 0.6);
        this.boatG.rotation.x = 0;
      } else if (tb >= 0 && tb < 1.4) {
        this.crest.visible = true;
        this.crest.position.set(0, this.seaY + 0.7 - tb * 1.6, L.bow * K - 1 + tb * 14);
      } else this.crest.visible = false;
      if (tw >= 0 && tw < 3.2 && Math.floor(now * 6) !== Math.floor((now - 0.016) * 6) && this.crest.visible) {
        const z = this.crest.position.z;
        for (let i = 0; i < 3; i++) this.ctx.vfx.burst(new THREE.Vector3((this.rnd() - 0.5) * 30, this.crest.position.y + 1.2, z), { n: 3, color: '#eef3f8', speed: 0.03, up: 0.08, size: 0.26, life: 22, gravity: 0.006, spread: 0.8 });
      }
    }
    // The rope from the bow's coil to the hero while it swings.
    if (this.rope) {
      const h = s.heroes[0];
      const hp = h ? this.ctx.pos(h.id) : undefined;
      if (w.to !== -1 && hp) {
        const a = new THREE.Vector3(0, 0.6, L.bow * K + 1.4);
        const b = hp.clone().setY(hp.y + 1.1);
        const m = new THREE.Matrix4();
        const q = new THREE.Quaternion();
        const n = 14;
        for (let i = 0; i < n; i++) {
          const t0 = i / n;
          const t1 = (i + 1) / n;
          const p0 = a.clone().lerp(b, t0).setY(a.y + (b.y - a.y) * t0 + Math.sin(Math.PI * t0) * 0.9);
          const p1 = a.clone().lerp(b, t1).setY(a.y + (b.y - a.y) * t1 + Math.sin(Math.PI * t1) * 0.9);
          const mid = p0.clone().add(p1).multiplyScalar(0.5);
          q.setFromUnitVectors(new THREE.Vector3(0, 0, 1), p1.clone().sub(p0).normalize());
          m.compose(mid, q, new THREE.Vector3(1, 1, p0.distanceTo(p1)));
          this.rope.setMatrixAt(i, m);
        }
        this.rope.count = n;
        this.rope.instanceMatrix.needsUpdate = true;
      } else this.rope.count = 0;
    }
    // Who the water took: a head bobbing out from where it went, until hauled back or lost.
    let k = 0;
    for (const p of w.swept) {
      const m = this.heads[k];
      if (!m) break;
      if (p.lost || p.until < 0) continue;
      const out = p.x >= 0 ? 1 : -1;
      m.position.set(p.x * K + out * 1.6, this.seaY + 0.1 + Math.sin(now * 3 + k) * 0.08, p.y * K + 0.6);
      m.visible = true;
      this.glow.add(m.position.x, m.position.y + 0.4, m.position.z, RED, 1.2 + 0.4 * Math.sin(now * 8), 0.5);
      k++;
    }
    for (; k < this.heads.length; k++) (this.heads[k] as THREE.Mesh).visible = false;
  }

  /** The act 3 world: the dark water easing in and out, the look's red at its tip, the leap's shadow, the Grey Wolf. */
  private frameElder(now: number, dt: number): void {
    const s = this.last;
    const r = s?.rules?.senda?.bote?.elder;
    const w = s?.senda?.bote?.elder;
    if (!s || !r || !w || !this.sea) return;
    const dp = (this.sea.uniforms.darkP as THREE.IUniform<THREE.Vector3[]>).value;
    r.deep.forEach((d, i) => {
      const on = (w.dark >> i) & 1;
      this.darkK[i] = (this.darkK[i] as number) + (on - (this.darkK[i] as number)) * Math.min(1, dt * 0.8);
      (dp[i] as THREE.Vector3).set(d.x * K, d.y * K, this.darkK[i] as number);
    });
    // The look and the swim: the tip it goes for burning red, a dotted line from it.
    if ((w.phase === 1 || w.phase === 2) && w.tip >= 0) {
      const t = r.tips[w.tip] as CazaPt;
      const pulse = 0.5 + 0.5 * Math.sin(now * 12);
      this.glow.add(t.x * K, 0.5, t.y * K, RED, 2.6 + pulse * 1.2, 0.5 + 0.4 * pulse);
      const e = s.enemies.find((x) => x.id === w.id);
      const ep = e ? (this.ctx.pos(e.id) ?? this.ctx.at(e.x, e.y, 0)) : null;
      if (ep && Math.floor(now * 5) !== Math.floor((now - dt) * 5))
        for (let i = 1; i < 6; i++) this.ctx.vfx.ring(ep.clone().lerp(this.v3.set(t.x * K, 0.15, t.y * K), i / 6).setY(0.15), 0.25, 0.1, '#ff4f3a', 6, 0.7);
    }
    // The leap's shadow, growing as it comes.
    if (this.shadow) {
      if (w.leapT >= 0) {
        const k = 1 - Math.max(0, (w.leapT - s.tick) / Math.max(1, r.leapWarn));
        this.shadow.visible = true;
        this.shadow.position.set(w.leapAt.x * K, this.ctx.ground(w.leapAt.x * K, w.leapAt.y * K) + 0.08, w.leapAt.y * K);
        this.shadow.scale.setScalar((r.leapR * K) * (0.55 + 0.45 * k));
        this.glow.add(this.shadow.position.x, 0.3, this.shadow.position.z, RED, 1.6 + k, 0.25 + 0.3 * k);
      } else this.shadow.visible = false;
    }
    // The Grey Wolf circling far off, half under.
    if (this.gray) {
      const a = now * 0.07;
      this.gray.position.set(Math.cos(a) * 34, this.seaY + Math.sin(now * 1.6) * 0.1, Math.sin(a) * 26);
      this.gray.rotation.y = -a;
    }
  }

  frame(now: number, camera?: THREE.Camera, alpha = 1): void {
    const dt = this.now ? Math.min(0.1, now - this.now) : 0;
    this.now = now;
    this.dome?.frame(now, camera);
    if (camera) lookPoint(camera, this.look);
    this.rain?.frame(now, this.look);
    if (this.sea) (this.sea.uniforms.time as THREE.IUniform<number>).value = now;
    this.drawTerron(this.last, alpha);
    this.glow.begin();
    if (this.place === 'boteWake') this.frameWake(now, dt);
    else if (this.place === 'boteSwell') this.frameSwell(now);
    else if (this.place === 'boteElder') this.frameElder(now, dt);
    // The boat rides the sea: a slow roll and pitch (more in the storm). The figures stand on the board, so a little only.
    const rough = this.hour === 'storm' ? 1 : 0.4;
    this.boatG.position.y = Math.sin(now * 1.2) * 0.03 * rough;
    // The bow wave and the wake astern, now and then.
    if (this.place !== 'boteCamp' && now > this.nextFoam) {
      this.nextFoam = now + (this.ctx.low ? 1.2 : 0.7);
      const L = boatLayout();
      for (const k of [-1, 1]) this.ctx.vfx.burst(new THREE.Vector3(k * 0.9, this.seaY + 0.15, L.bow * K + 0.8), { n: 3, color: '#eef3f8', speed: 0.03, up: 0.05, size: 0.22, life: 22, gravity: 0.005, spread: 0.6 });
    }
    // Lightning in the storm: the sea and the sky flash.
    if (this.hour === 'storm') {
      if (now > this.nextFlash) {
        this.flashAt = now;
        this.nextFlash = now + 4 + this.rnd() * 6;
      }
      const f = Math.max(0, 1 - (now - this.flashAt) / 0.35) * (0.6 + 0.4 * Math.sin((now - this.flashAt) * 60));
      if (this.sea) (this.sea.uniforms.flash as THREE.IUniform<number>).value = Math.max(0, f);
      const { hemi } = this.ctx;
      if (hemi) hemi.intensity = HOURS.storm.hemi[2] + Math.max(0, f) * 1.6;
    }
    if (this.calmWolf) this.calmWolf.position.y = 0.02 + Math.sin(now * 0.8) * 0.005;
    if (this.embers.length && now > this.nextEmber) {
      this.nextEmber = now + (this.ctx.low ? 0.35 : 0.18);
      const e = this.embers[Math.floor(this.rnd() * this.embers.length)] as THREE.Vector3;
      this.ctx.vfx.burst(e, { n: 1, color: '#ff9a5a', speed: 0.006, up: 0.03, size: 0.05, life: 30, gravity: -0.0008, spread: 0.2 });
    }
    for (const l of this.lights) {
      const fl = l.flick ? 0.84 + 0.16 * Math.sin(now * (6 + l.seed) + l.seed) * l.flick + 0.06 * Math.sin(now * 17 + l.seed * 3) * l.flick : 1;
      this.glow.add(l.p.x, l.p.y, l.p.z, l.c, l.size, l.a * fl);
    }
    if (this.bankAt > 0 && now - this.bankAt < 1.5) this.glow.add(this.look.x, this.look.y + 6, this.look.z, COLD, 26, (1.5 - (now - this.bankAt)) * 0.05);
    this.glow.end();
    void RED_SOFT;
  }

  dispose(): void {
    if (this.fork) this.fork.g.removeFromParent();
    if (this.usesIce) releaseIceTexture();
    for (const m of this.mats) m.dispose();
    for (const g of this.geos) g.dispose();
    this.dome?.dispose();
    this.rain?.dispose();
    this.glow.mesh.geometry.dispose();
    this.glow.material.dispose();
    this.group.removeFromParent();
  }
}
